// Reportes Operativos — Coordinador crea (busca un envío por tracking,
// abre el mismo OverrideEditModal en mode="report") y ve sus propios
// reportes; Liquidaciones/Admin Total ven la cola completa y
// aprueban/rechazan. Un solo componente, comportamiento distinto según
// perfil (ver informe "Sprint Operativo", Parte 6).
import React, { useEffect, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import OverrideEditModal, { money, REPORT_TYPE_LABELS, ReportType, ReportTarget } from "../components/liquidaciones/OverrideEditModal";

type ReportStatus = "PENDIENTE" | "APROBADO" | "RECHAZADO";

type ReportRow = {
  id: number;
  lightdata_id: number | string;
  tracking: string | null;
  store_id: number | null;
  store_name: string | null;
  driver_id: number | null;
  driver_name: string | null;
  manual_price: number | null;
  force_turbo: boolean;
  double_price: boolean;
  report_type: ReportType;
  observation: string | null;
  status: ReportStatus;
  requested_by_name: string | null;
  requested_at: string;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
};

type LookupResult = {
  lightdataId: number | string;
  tracking: string;
  direccion: string | null;
  estado: string | null;
  store: { id: number; name: string | null } | null;
  driver: { id: number; name: string | null } | null;
};

const STATUS_BADGE: Record<ReportStatus, { label: string; color: string }> = {
  PENDIENTE: { label: "Pendiente", color: "#c99a1f" },
  APROBADO: { label: "Aprobado", color: "var(--success, #2ea043)" },
  RECHAZADO: { label: "Rechazado", color: "var(--danger, #d1242f)" },
};

const pageWrapStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "16px" };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "26px", fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-0.02em" };
const subtitleStyle: React.CSSProperties = { margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "14px" };
const tableWrapStyle: React.CSSProperties = { overflowX: "auto", border: "1px solid var(--border)", borderRadius: "18px" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px", color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", background: "rgba(255,255,255,0.02)" };
const tdStyle: React.CSSProperties = { padding: "10px", color: "var(--text-primary)", fontSize: "13px", borderBottom: "1px solid var(--border)" };
const emptyStyle: React.CSSProperties = { padding: "32px 12px", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };

function StatusBadge({ status }: { status: ReportStatus }) {
  const b = STATUS_BADGE[status];
  return (
    <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, border: `1px solid ${b.color}`, color: b.color }}>
      {b.label}
    </span>
  );
}

// Búsqueda de envío por tracking + elección de a quién apunta el reporte
// (Store o Driver, si el envío resuelve a ambos) — exclusivo Coordinador.
function CoordinadorSearch({ token, onOpenReport }: { token: string | null; onOpenReport: (target: ReportTarget, label: string) => void }) {
  const [tracking, setTracking] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LookupResult | null>(null);

  const search = async () => {
    if (!tracking.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await authFetch(`/v1/reports/lookup/${encodeURIComponent(tracking.trim())}`, { token });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "No se encontró el envío");
      setResult(payload.envio);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 10px 0", fontSize: "15px", fontWeight: 700 }}>Reportar un envío</h3>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ minWidth: "220px" }}>
          <Input
            label="Tracking"
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            placeholder="Ej: 48053632173"
            onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && search()}
          />
        </div>
        <Button onClick={search} disabled={loading || !tracking.trim()}>{loading ? "Buscando…" : "Buscar"}</Button>
      </div>
      {error && <div style={{ color: "var(--danger, #d1242f)", fontSize: "13px", marginTop: "10px" }}>{error}</div>}
      {result && (
        <div style={{ marginTop: "14px", padding: "12px 14px", borderRadius: "12px", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: "13px", marginBottom: "8px" }}>
            <div><span style={{ color: "var(--text-secondary)" }}>Dirección: </span>{result.direccion || "Sin informar"}</div>
            <div><span style={{ color: "var(--text-secondary)" }}>Estado: </span>{result.estado || "—"}</div>
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {result.store && (
              <Button
                variant="secondary"
                onClick={() => onOpenReport({ lightdataId: result.lightdataId, storeId: result.store!.id }, `Tienda: ${result.store!.name || "—"}`)}
              >
                Reportar como Tienda: {result.store.name || "—"}
              </Button>
            )}
            {result.driver && (
              <Button
                variant="secondary"
                onClick={() => onOpenReport({ lightdataId: result.lightdataId, driverId: result.driver!.id }, `Chofer: ${result.driver!.name || "—"}`)}
              >
                Reportar como Chofer: {result.driver.name || "—"}
              </Button>
            )}
            {result.store && result.driver && (
              // "Ambos" (ver informe "LYNX — Ajustes finales de Liquidaciones +
              // Auditoría de Performance", Parte 4): un solo reporte impacta a
              // la vez sobre Store Y Driver — el transporte ya soporta mandar
              // ambos ids juntos (ver OverrideEditModal.tsx mode="report"), acá
              // solo faltaba la opción.
              <Button
                onClick={() =>
                  onOpenReport(
                    { lightdataId: result.lightdataId, storeId: result.store!.id, driverId: result.driver!.id },
                    `Tienda: ${result.store!.name || "—"} + Chofer: ${result.driver!.name || "—"}`
                  )
                }
              >
                Reportar como Tienda + Chofer
              </Button>
            )}
            {!result.store && !result.driver && (
              <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Este envío no está vinculado a ninguna Tienda ni Driver todavía.</span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function ReportesOperativos() {
  const { token, adminProfile, financeAccess } = useAuth();
  const isReviewer = adminProfile === "LIQUIDACIONES" || adminProfile === "ADMIN_TOTAL";
  // Ver auditoría de cierre "LYNX Fase 1", Bloqueador 2: el backend exige
  // finance_access específicamente para aprobar/rechazar (ver
  // operationalReports.routes.js, PUT .../approve y .../reject) — distinto
  // de isReviewer (perfil), que solo decide si ve la cola completa de
  // TODOS los reportes o únicamente los propios. Un LIQUIDACIONES/
  // ADMIN_TOTAL sin finance_access debe seguir viendo la cola (isReviewer
  // sigue intacto), pero sin columna de acciones.
  const canApprove = isReviewer && financeAccess;

  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportTarget, setReportTarget] = useState<{ target: ReportTarget; label: string } | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const loadReports = React.useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    return authFetch("/v1/reports", { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar los reportes");
        setReports(payload.reports || []);
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const approve = async (id: number) => {
    setActingId(id);
    try {
      const res = await authFetch(`/v1/reports/${id}/approve`, { method: "PUT", token, json: true, body: "{}" });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "No se pudo aprobar");
      await loadReports();
    } catch (e: any) {
      alert(e.message || "No se pudo aprobar el reporte");
    } finally {
      setActingId(null);
    }
  };

  const reject = async (id: number) => {
    setActingId(id);
    try {
      const res = await authFetch(`/v1/reports/${id}/reject`, {
        method: "PUT", token, json: true,
        body: JSON.stringify({ rejection_reason: rejectReason.trim() || null }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "No se pudo rechazar");
      setRejectingId(null);
      setRejectReason("");
      await loadReports();
    } catch (e: any) {
      alert(e.message || "No se pudo rechazar el reporte");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div style={pageWrapStyle}>
      <div>
        <h1 style={titleStyle}>Reportes Operativos</h1>
        <p style={subtitleStyle}>
          {canApprove
            ? "Cola de solicitudes — aprobar aplica el cambio, rechazar no toca nada."
            : isReviewer
              ? "Cola de solicitudes — consulta. Aprobar/Rechazar requiere permiso financiero."
              : "Tus reportes — quedan pendientes hasta que Liquidaciones los revise."}
        </p>
      </div>

      {!isReviewer && (
        <CoordinadorSearch
          token={token}
          onOpenReport={(target, label) => setReportTarget({ target, label })}
        />
      )}

      <Card padding="0">
        {loading && <div style={emptyStyle}>Cargando…</div>}
        {!loading && error && <div style={{ ...emptyStyle, color: "var(--danger, #d1242f)" }}>{error}</div>}
        {!loading && !error && reports.length === 0 && <div style={emptyStyle}>Sin reportes todavía.</div>}
        {!loading && !error && reports.length > 0 && (
          <div style={{ ...tableWrapStyle, border: "none", borderRadius: 0 }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tracking</th>
                  <th style={thStyle}>Entidad</th>
                  <th style={thStyle}>Motivo</th>
                  <th style={thStyle}>Solicitado</th>
                  <th style={thStyle}>Precio propuesto</th>
                  <th style={thStyle}>Estado</th>
                  {canApprove && <th style={thStyle}></th>}
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id} title={r.observation || undefined}>
                    <td style={tdStyle}>{r.tracking || r.lightdata_id}</td>
                    <td style={tdStyle}>
                      {/* "Ambos" (ver informe "LYNX — Ajustes finales de Liquidaciones + Auditoría
                          de Performance", Parte 4): un reporte puede tener store_name Y driver_name
                          a la vez — mostrar los dos, nunca solo el primero que matchee. */}
                      {[r.store_name ? `Tienda: ${r.store_name}` : null, r.driver_name ? `Chofer: ${r.driver_name}` : null].filter(Boolean).join(" + ") || "—"}
                    </td>
                    <td style={tdStyle}>
                      {REPORT_TYPE_LABELS[r.report_type]}
                      {r.force_turbo && " · ⚡"}
                      {r.double_price && " · ×2"}
                    </td>
                    <td style={tdStyle}>{r.requested_by_name || "—"}</td>
                    <td style={tdStyle}>{r.manual_price !== null ? money(r.manual_price) : "—"}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={r.status} />
                      {r.status === "RECHAZADO" && r.rejection_reason && (
                        <div style={{ fontSize: "11px", color: "var(--text-secondary)", marginTop: "2px" }}>{r.rejection_reason}</div>
                      )}
                    </td>
                    {canApprove && (
                      <td style={tdStyle}>
                        {r.status === "PENDIENTE" && (
                          rejectingId === r.id ? (
                            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                              <input
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Motivo del rechazo…"
                                style={{ fontSize: "12px", padding: "4px 8px", borderRadius: "8px", border: "1px solid var(--border)", background: "transparent", color: "var(--text-primary)" }}
                              />
                              <Button variant="danger" onClick={() => reject(r.id)} disabled={actingId === r.id}>Confirmar</Button>
                              <Button variant="secondary" onClick={() => { setRejectingId(null); setRejectReason(""); }}>Cancelar</Button>
                            </div>
                          ) : (
                            <div style={{ display: "flex", gap: "6px" }}>
                              <Button onClick={() => approve(r.id)} disabled={actingId === r.id}>{actingId === r.id ? "…" : "Aprobar"}</Button>
                              <Button variant="secondary" onClick={() => setRejectingId(r.id)} disabled={actingId === r.id}>Rechazar</Button>
                            </div>
                          )
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <OverrideEditModal
        open={reportTarget !== null}
        mode="report"
        entityLabel={reportTarget?.label ?? null}
        token={token}
        delivery={
          reportTarget
            ? {
                lightdata_id: reportTarget.target.lightdataId,
                tracking: null,
                fecha_colecta: null,
                destinatario: null,
                direccion: null,
                original_cp: null,
                cp: null,
                has_postal_code_override: false,
                tier: null,
                tariff_name: null,
                estado: null,
                is_turbo: false,
                turbo_source: null,
                turbo_price: null,
                is_double: false,
                calculated_price: null,
                manual_price: null,
                final_price: null,
                pricing_status: "PENDING",
                pricing_reason: null,
                has_override: false,
                override_observation: null,
              }
            : null
        }
        reportTarget={reportTarget?.target}
        onClose={() => setReportTarget(null)}
        onSaved={() => {
          setReportTarget(null);
          loadReports();
        }}
      />
    </div>
  );
}
